import React from 'react';
import { formatCurrencyAmount } from '../../../../../../utils/miscFunctions';

/**
 * Per-vehicle premium breakdown for fleet flows. Rendered on both the
 * Quote (Premium Details) step AND the Policy Review step so operators
 * get the same drill-in on either screen. Kept in one component so the
 * layout, source-resolution priority and empty-state behaviour don't
 * drift between the two surfaces.
 *
 * Sources checked in order:
 *  1. explicit `vehicleQuotes` prop — highest priority; the caller
 *     already knows the priced quote breakdown (usually the
 *     FleetQuoteResponse.vehicleQuotes echoed from the BE).
 *  2. `wizardData.vehicles` — synthesise a row per uploaded/manually-
 *     entered vehicle when the priced breakdown isn't available yet so
 *     the operator at least sees the fleet shape at review time.
 */
interface FleetVehicleBreakdownProps {
  vehicleQuotes?: any[];
  /**
   * Fallback vehicle list — usually wizardData.vehicles (the CSV / manual
   * entries). Rows synthesised from this source lack priced-premium
   * fields but still surface reg / make / model / dates / cover.
   */
  fallbackVehicles?: any[];
  /**
   * When false the component renders nothing. Callers gate on their own
   * "is this a fleet flow" signal (isFleetJourney / vehicleType === 'fleet')
   * so this prop just makes it explicit.
   */
  enabled?: boolean;
  /**
   * Details block collapsed vs open on mount. Defaults to open — the
   * quote / review flows want the breakdown visible immediately.
   */
  defaultOpen?: boolean;
}

const FleetVehicleBreakdown: React.FC<FleetVehicleBreakdownProps> = ({
  vehicleQuotes,
  fallbackVehicles,
  enabled = true,
  defaultOpen = true,
}) => {
  if (!enabled) return null;

  let rows: any[] = [];
  if (Array.isArray(vehicleQuotes) && vehicleQuotes.length > 0) {
    rows = vehicleQuotes;
  } else if (Array.isArray(fallbackVehicles) && fallbackVehicles.length > 0) {
    rows = fallbackVehicles.map((v: any) => ({
      vehicleRegistration: v?.registrationNumber,
      make: v?.maker,
      model: v?.model,
      insurerPortfolioId: v?.insurerPortfolioId,
      startDate: v?.startDate,
      endDate: v?.endDate,
      vehiclePremium: v?.premium,
      netPremium: v?.netPremium,
    }));
  }
  if (rows.length === 0) return null;

  return (
    <details className="mt-3 rounded-md border border-gray-200 dark:border-darkBg-border" open={defaultOpen}>
      <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-gray-700 dark:text-darkBg-mainText">
        Per-vehicle premium breakdown ({rows.length} vehicles)
      </summary>
      <div className="border-t border-gray-200 dark:border-darkBg-border">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 dark:bg-darkBg text-gray-600 dark:text-darkBg-subText">
            <tr>
              <th className="px-3 py-2 text-left">Reg.</th>
              <th className="px-3 py-2 text-left">Make / Model</th>
              <th className="px-3 py-2 text-left">Cover</th>
              <th className="px-3 py-2 text-left">Start</th>
              <th className="px-3 py-2 text-left">End</th>
              <th className="px-3 py-2 text-right">Premium</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((vq: any, i: number) => (
              <tr key={`${vq.vehicleRegistration ?? i}`} className="border-t border-gray-100 dark:border-darkBg-border">
                <td className="px-3 py-2 dark:text-darkBg-mainText">{vq.vehicleRegistration || '-'}</td>
                <td className="px-3 py-2 dark:text-darkBg-mainText">{[vq.make, vq.model].filter(Boolean).join(' ') || '-'}</td>
                <td className="px-3 py-2 dark:text-darkBg-mainText">{vq.insurerPortfolioId ?? '-'}</td>
                <td className="px-3 py-2 dark:text-darkBg-mainText">{vq.startDate || '-'}</td>
                <td className="px-3 py-2 dark:text-darkBg-mainText">{vq.endDate || '-'}</td>
                <td className="px-3 py-2 text-right dark:text-darkBg-mainText">
                  {vq.netPremium != null
                    ? formatCurrencyAmount(Number(vq.netPremium))
                    : (vq.vehiclePremium != null ? formatCurrencyAmount(Number(vq.vehiclePremium)) : '-')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
};

export default FleetVehicleBreakdown;
